import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { T } from '@tolgee/react';
import { useCart } from '../../../context/CartContext';
import { useAuth } from '../../../context/AuthContext';
import { motion } from 'framer-motion';
import { useZarazTracking } from '../../../lib/zaraz/useZarazTracking';
import api from '../../../lib/api';

const CartPage = () => {
  console.log('🛒 [CartPage] Component rendering');
  const { cart, refetch } = useCart();
  const { auth } = useAuth();
  const [isShowingConfirmation, setIsShowingConfirmation] = useState(false);
  const [fbt, setFbt] = useState<any[]>([]);
  const { trackCartViewed } = useZarazTracking();

  const loading = cart.isLoading;
  const error = null; // CartContext doesn't expose error state

  console.log('🛒 [CartPage] Current state - loading:', loading, 'cart items:', cart?.items?.length);

  // No refetch on mount - the CartContext already fetches on initial load
  // and has listeners for focus/visibility/bfcache that will refresh when needed
  useEffect(() => {
    console.log('🛒 [CartPage] Mounted, cart has', cart.items.length, 'items, totalItems:', cart.totalItems);
  }, []);

  const handleQuantityChange = async (productId: string, newQuantity: number, variantSku?: string) => {
    console.log('🛒 [CartPage] handleQuantityChange:', productId, newQuantity, variantSku);
    try {
      const response = await api.cart.updateCartItem(productId, newQuantity, variantSku);
      console.log('🛒 [CartPage] updateCartItem response:', response);
      // Pass the updated cart data in the event to avoid another GET request
      if (response.success && response.data) {
        window.dispatchEvent(new CustomEvent('jarad-cart-updated', { detail: response.data }));
      }
    } catch (error) {
      console.error('Error updating cart item:', error);
    }
  };

  const handleRemoveItem = async (productId: string, variantSku?: string) => {
    console.log('🛒 [CartPage] handleRemoveItem:', productId, variantSku);
    try {
      const response = await api.cart.removeFromCart(productId, variantSku);
      if (response.success && response.data) {
        window.dispatchEvent(new CustomEvent('jarad-cart-updated', { detail: response.data }));
      }
    } catch (error) {
      console.error('Error removing cart item:', error);
    }
  };

  const handleClearCart = () => {
    setIsShowingConfirmation(true);
  };

  const confirmClearCart = async () => {
    try {
      const response = await api.cart.clearCart();
      if (response.success && response.data) {
        window.dispatchEvent(new CustomEvent('jarad-cart-updated', { detail: response.data }));
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
    setIsShowingConfirmation(false);
  };

  const cancelClearCart = () => {
    setIsShowingConfirmation(false);
  };

  // Cargar recomendaciones "frecuentemente comprados juntos" según el carrito
  useEffect(() => {
    const fetchFbt = async () => {
      try {
        if (cart.items.length === 0) {
          setFbt([]);
          return;
        }
        const ids = cart.items.map((it) => it.productId).filter(Boolean);
        if (ids.length === 0) return;
        const res = await api.client.get<any>('/products/fbt', { params: { ids: ids.join(',') } });
        if (res.success && res.data) setFbt(res.data as any[]);
      } catch {
        // ignorar errores de recomendaciones
      }
    };
    fetchFbt();
  }, [cart?.items.map(i => i.productId).join(',')]);

  // Trackear vista del carrito con productos reales
  useEffect(() => {
    const trackCart = async () => {
      if (cart.items.length > 0) {
        try {
          // Obtener productos reales del carrito
          const productPromises = cart.items.map(item =>
            api.products.getProduct(item.productId)
          );
          const productResponses = await Promise.all(productPromises);

          const cartProducts = cart.items.map((item, index) => {
            const productRes = productResponses[index];
            const product = productRes.success && productRes.data ? productRes.data : null;

            if (!product) {
              // Si no se pudo obtener el producto, usar datos básicos del item
              console.warn(`No se pudo cargar producto ${item.productId} para tracking`);
              return null;
            }

            return {
              product,
              variant: item.variantTitle ? {
                name: item.variantTitle,
                sku: item.variantId || '',
                price: item.price || 0,
                stock: 0,
                isActive: true
              } : null,
              quantity: item.quantity
            };
          }).filter(Boolean); // Filtrar nulls

          if (cartProducts.length > 0) {
            trackCartViewed(cartProducts as any);
          }
        } catch (error) {
          console.error('Error al cargar productos para tracking del carrito:', error);
        }
      }
    };

    trackCart();
  }, [cart?.items.map(i => i.productId).join(','), trackCartViewed]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-pulse text-gray-400">
          <T keyName="cart.loading">Cargando carrito...</T>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center py-16">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
            <T keyName="cart.error_loading">Error al cargar el carrito</T>
          </h2>
          <p className="text-lg text-gray-500 mb-8">{error}</p>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
          >
            <T keyName="common.retry">Reintentar</T>
          </button>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center py-16">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
            <T keyName="cart.empty_cart">Tu carrito está vacío</T>
          </h2>
          <p className="text-lg text-gray-500 mb-8">
            <T keyName="cart.empty_cart_message">Parece que aún no has agregado productos a tu carrito.</T>
          </p>
          <Link
            to="/products"
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
          >
            <T keyName="cart.continue_shopping">Seguir comprando</T>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="cart-page">
      <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 mb-8" data-testid="cart-title">
        <T keyName="cart.your_cart">Tu carrito</T>
      </h1>

      {/* Banner de pedido mínimo */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <span className="font-medium"><T keyName="cart.minimum_order">Pedido mínimo:</T></span>{' '}
              <T keyName="cart.minimum_order_message">El pedido mínimo es de 1 pallet de productos.</T>
            </p>
          </div>
        </div>
      </div>

      {/* Confirmación para limpiar carrito */}
      {isShowingConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            className="bg-white rounded-lg p-6 max-w-md mx-auto"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              <T keyName="cart.confirm_clear">¿Estás seguro?</T>
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              <T keyName="cart.confirm_clear_message">Se eliminarán todos los productos de tu carrito.</T>
            </p>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
                onClick={cancelClearCart}
              >
                <T keyName="common.cancel">Cancelar</T>
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none"
                onClick={confirmClearCart}
              >
                <T keyName="cart.yes_clear_cart">Sí, vaciar carrito</T>
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            <T keyName="cart.items_count" params={{ count: cart.totalItems.toString() }}>
              {`${cart.totalItems} ${cart.totalItems === 1 ? 'producto' : 'productos'} en tu carrito`}
            </T>
          </h2>
          <button
            type="button"
            className="text-sm font-medium text-red-600 hover:text-red-500"
            onClick={handleClearCart}
            disabled={cart.items.length === 0}
            data-testid="clear-cart-button"
          >
            <T keyName="cart.clear_cart">Vaciar carrito</T>
          </button>
        </div>
        <ul className="divide-y divide-gray-200">
          {cart.items.map((item) => {
            const hasInvalidPrice = !item.price || item.price <= 0;
            return (
            <li key={`${item.productId}_${item.variantId || 'default'}`} className={`px-4 py-6 sm:px-6 ${hasInvalidPrice ? 'bg-red-50 border-l-4 border-red-400' : ''}`} data-testid="cart-item">
              {hasInvalidPrice && (
                <div className="flex items-center justify-between mb-3 p-2 bg-red-100 rounded-md">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-red-600 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium text-red-800">
                      Este producto tiene precio incorrecto (0,00€). Elimínalo para poder continuar.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="ml-3 px-3 py-1.5 text-sm font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none"
                    onClick={() => handleRemoveItem(item.productId, item.variantId)}
                  >
                    Eliminar
                  </button>
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:items-center">
                {/* Imagen del producto + info básica en móvil */}
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-md overflow-hidden">
                    <img
                      src={item.imageUrl || '/placeholder-product.jpg'}
                      alt={item.title}
                      className="w-full h-full object-center object-cover"
                      data-testid="cart-item-image"
                    />
                  </div>
                  <div className="ml-4 sm:hidden flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      <Link to={`/products/${item.productId}`} className="hover:underline" data-testid="cart-item-name">
                        {item.title}
                      </Link>
                    </h3>
                    {item.variantTitle && (
                      <p className="text-xs text-gray-600 mt-0.5" data-testid="cart-item-variant">
                        <T keyName="cart.variant">Variante</T>: {item.variantTitle}
                      </p>
                    )}
                    <div className="text-sm font-medium text-gray-900 mt-1">
                      {item.priceBeforeDiscount && item.priceBeforeDiscount > item.price ? (
                        <>
                          <span className="text-xs text-gray-400 line-through mr-1">{(item.priceBeforeDiscount * item.quantity).toFixed(2)}€</span>
                          <span data-testid="cart-item-total">{((item.price || 0) * item.quantity).toFixed(2)}€</span>
                        </>
                      ) : (
                        <span data-testid="cart-item-total">{((item.price || 0) * item.quantity).toFixed(2)}€</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Información del producto - desktop */}
                <div className="sm:ml-6 flex-1 flex flex-col mt-3 sm:mt-0">
                  <div className="hidden sm:flex justify-between text-base font-medium text-gray-900">
                    <div>
                      <h3>
                        <Link to={`/products/${item.productId}`} className="hover:underline" data-testid="cart-item-name">
                          {item.title}
                        </Link>
                      </h3>
                      {item.variantTitle && (
                        <p className="text-sm text-gray-600 mt-1" data-testid="cart-item-variant">
                          <T keyName="cart.variant">Variante</T>: {item.variantTitle}
                        </p>
                      )}
                    </div>
                    <div className="ml-4 text-right">
                      {item.priceBeforeDiscount && item.priceBeforeDiscount > item.price ? (
                        <>
                          <p className="text-sm text-gray-400 line-through">{(item.priceBeforeDiscount * item.quantity).toFixed(2)}€</p>
                          <p className="font-medium text-gray-900" data-testid="cart-item-total">{((item.price || 0) * item.quantity).toFixed(2)}€</p>
                          {item.discountInfo && (
                            <span className="inline-block text-xs font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">{item.discountInfo.displayValue}</span>
                          )}
                        </>
                      ) : (
                        <p data-testid="cart-item-total">{((item.price || 0) * item.quantity).toFixed(2)}€</p>
                      )}
                    </div>
                  </div>

                  {/* Controles de cantidad y eliminar */}
                  <div className="flex items-center justify-between mt-2 sm:mt-4">
                    <div className="flex flex-col">
                      {/* Packaging info if available */}
                      {item.packaging?.presentation && (
                        <p className="text-xs text-blue-600 mb-1">
                          {item.packaging.presentation}
                          {(item.packaging?.minimumQuantity || 1) > 1 && (
                            <span className="ml-1 text-orange-600">
                              • Mín. {item.packaging.minimumQuantity}
                            </span>
                          )}
                        </p>
                      )}
                      <div className="flex items-center">
                        {/* Quantity controls with step */}
                        <div className="flex items-center border border-gray-300 rounded-md">
                          <button
                            type="button"
                            onClick={() => {
                              const step = item.packaging?.quantityStep || 1;
                              const min = item.packaging?.minimumQuantity || 1;
                              const newQty = Math.max(min, item.quantity - step);
                              handleQuantityChange(item.productId, newQty, item.variantId);
                            }}
                            disabled={item.quantity <= (item.packaging?.minimumQuantity || 1)}
                            className="px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="cart-item-decrease"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                          </button>
                          <span
                            className="px-3 py-1 text-sm font-medium text-gray-900 min-w-[3rem] text-center"
                            data-testid="cart-item-quantity"
                          >
                            {item.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const step = item.packaging?.quantityStep || 1;
                              handleQuantityChange(item.productId, item.quantity + step, item.variantId);
                            }}
                            className="px-2 py-1 text-gray-600 hover:bg-gray-100"
                            data-testid="cart-item-increase"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>
                        <div className="text-sm font-medium text-gray-500 ml-2 hidden sm:block" data-testid="cart-item-price">
                          {item.priceBeforeDiscount && item.priceBeforeDiscount > item.price ? (
                            <>
                              <span className="line-through text-gray-400 mr-1">{item.priceBeforeDiscount.toFixed(2)}€</span>
                              <span className="text-gray-900">{(item.price || 0).toFixed(2)}€</span>
                            </>
                          ) : (
                            <span>{(item.price || 0).toFixed(2)}€</span>
                          )}
                          {' '}<T keyName="cart.per_unit">/ unidad</T>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="flex-shrink-0 ml-4 text-sm font-medium text-red-600 hover:text-red-500"
                      onClick={() => handleRemoveItem(item.productId, item.variantId)}
                      data-testid="remove-item-button"
                    >
                      <T keyName="cart.remove_item">Eliminar</T>
                    </button>
                  </div>
                </div>
              </div>
            </li>
            );
          })}
        </ul>
      </div>

      {/* Recomendados frecuentemente juntos */}
      {fbt.length > 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              <T keyName="cart.frequently_bought_together">Frecuentemente comprados juntos</T>
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              <T keyName="cart.related_products_message">Te pueden interesar estos productos relacionados</T>
            </p>
          </div>
          <div className="px-4 sm:px-6 pb-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {fbt.map((p) => (
                <Link key={(p as any)._id || p.id} to={`/products/${(p as any)._id || p.id}`} className="block group">
                  <div className="aspect-square rounded-md overflow-hidden bg-gray-100">
                    <img
                      src={p.images?.[0]?.url || '/placeholder-product.jpg'}
                      alt={(p as any).name || (p as any).title}
                      className="w-full h-full object-cover group-hover:opacity-90"
                    />
                  </div>
                  <div className="mt-2 text-sm font-medium text-gray-900 truncate">{(p as any).name || (p as any).title}</div>
                  {typeof (p as any).price === 'number' && (
                    <div className="text-sm text-gray-700">€{(p as any).price.toFixed(2)}</div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Resumen y checkout */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            <T keyName="cart.order_summary">Resumen del pedido</T>
          </h2>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between mb-2">
            <p className="text-sm text-gray-500">
              <T keyName="cart.subtotal">Subtotal</T>
            </p>
            <p className="text-sm font-medium text-gray-900" data-testid="cart-subtotal">{cart.subtotal.toFixed(2)}€</p>
          </div>
          <div className="flex justify-between mb-2">
            <p className="text-sm text-gray-500">
              <T keyName="cart.shipping">Envío</T>
            </p>
            <p className="text-sm font-medium text-gray-900">
              <T keyName="cart.shipping_calculated_next">Calculado en el siguiente paso</T>
            </p>
          </div>
          <div className="border-t border-gray-200 my-4 pt-4">
            <div className="flex justify-between">
              <p className="text-base font-medium text-gray-900">
                <T keyName="cart.total_without_shipping">Total (sin envío)</T>
              </p>
              <p className="text-base font-medium text-gray-900" data-testid="cart-total">{cart.subtotal.toFixed(2)}€</p>
            </div>
            {auth.user?.prontoPagoDiscount != null && auth.user.prontoPagoDiscount > 0 && cart.subtotal > 0 && (
              <div className="flex justify-between mt-2 p-2 bg-amber-50 rounded-md border border-amber-200">
                <p className="text-sm text-amber-800">
                  <T keyName="cart.pronto_pago">{`Con pronto pago (${auth.user.prontoPagoDiscount}%)`}</T>
                </p>
                <p className="text-sm font-semibold text-amber-800">
                  {(cart.subtotal * (1 - auth.user.prontoPagoDiscount / 100)).toFixed(2)}€
                </p>
              </div>
            )}
          </div>
          {cart.items.some(i => !i.price || i.price <= 0) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm font-medium text-red-800">
                Tienes productos con precio incorrecto en el carrito. Elimínalos para poder continuar.
              </p>
            </div>
          )}
          <div className="mt-6">
            {cart.items.some(i => !i.price || i.price <= 0) ? (
              <button
                disabled
                className="block w-full text-center bg-gray-400 border border-transparent rounded-md shadow-sm py-3 px-4 text-base font-medium text-white cursor-not-allowed"
                data-testid="checkout-button"
              >
                <T keyName="cart.proceed_to_checkout">Proceder al pago</T>
              </button>
            ) : (
              <Link
                to="/checkout"
                className="block w-full text-center bg-black border border-transparent rounded-md shadow-sm py-3 px-4 text-base font-medium text-white hover:bg-gray-900 focus:outline-none"
                data-testid="checkout-button"
              >
                <T keyName="cart.proceed_to_checkout">Proceder al pago</T>
              </Link>
            )}
          </div>
          <div className="mt-4 text-center">
            <Link
              to="/products"
              className="text-sm font-medium text-gray-600 hover:text-gray-500"
            >
              <T keyName="cart.or_continue_shopping">o continuar comprando</T>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage; 